import { Button } from '@mantine/core';
import { IconArrowRight } from '@tabler/icons-react';
import { motion, stagger, type Variants } from 'motion/react';

import classes from './StepperCard.module.css';
import type { BaseSlideProps, SlideOption } from '#plugin-chat/components/tools/ask-user-question/schema.ts';
import { requiredKeys } from '#plugin-chat/lib/ui/streaming-condition.ts';
import { useStreamingSuspense } from '#plugin-chat/lib/ui/streaming-suspense.tsx';

const optionMotionVariants: Variants = {
  hidden: {
    opacity: 0,
  },
  visible: {
    opacity: 1,
    transition: {
      duration: 0.2,
      delayChildren: stagger(0.4),
      when: 'beforeChildren',
    },
  },
};

const optionMotionChildVariants: Variants = {
  hidden: {
    opacity: 0,
    y: -20,
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.3,
    },
  },
};

export interface OptionSlideProps extends BaseSlideProps {}

interface OptionButtonProps extends Pick<BaseSlideProps, 'onOptionClick' | 'isStreaming'> {
  option: SlideOption;
}

/**
 * Single option button that suspends while streaming.
 * Wrapped in Suspense by parent.
 */
function OptionButton({ option, onOptionClick, isStreaming = false }: OptionButtonProps) {
  // Suspend if streaming and option doesn't have required fields
  useStreamingSuspense(option, isStreaming, requiredKeys(['id', 'label']));

  return (
    <motion.div className={classes.actionButtonContainer} variants={optionMotionChildVariants}>
      <Button
        classNames={{ label: classes.optionButtonLabel, root: classes.optionButton }}
        color="dark.5"
        onClick={() => onOptionClick([option.id])}
        radius="sm"
        rightSection={<IconArrowRight size={16} />}
      >
        <span className={classes.optionContent}>
          <span className={classes.optionLabel}>{option.label}</span>
          {option.description && <span className={classes.optionDescription}>{option.description}</span>}
        </span>
      </Button>
    </motion.div>
  );
}

/** Radio-style option slide with staggered animation. */
export function OptionSlide({ options, onOptionClick, isStreaming }: OptionSlideProps) {
  return (
    <motion.div animate="visible" className={classes.options} initial="hidden" variants={optionMotionVariants}>
      {options
        ?.filter((option) => !!option.id)
        ?.map((option) => (
          <OptionButton isStreaming={isStreaming} key={option.id} onOptionClick={onOptionClick} option={option} />
        ))}
    </motion.div>
  );
}
